import { Target } from '@clearkit/icons';
import { CKContainerProps, CKLayoutHorizontal } from 'clearkit';
import { useContext, useEffect, useState } from 'react';

import { HomeBannerMatchRate } from '~/components/home/Banners';

// import useOnboardingStatus from '~/hooks/account/useOnboardingStatus';
import { MatchRateContext } from '../context/MatchRateContext';
import { HomeHeading } from '../HomeHeading';
import { HomeMatchRateCard } from '../HomeMatchRateCard';
import { useMatchRates } from './useMatchRates';

type HomeSectionMatchRateProps = CKContainerProps;

type MatchRateMetric = {
  with_data_count: number; // Number of records with data
  match_rate: number; // Match rate: with data / total
  missing_with_bad_emails_rate?: number; // Missing with bad emails rate: missing with bad emails / total missing
  attributes: any[];
};

type MatchRatesProps = {
  companyMetrics: MatchRateMetric | any;
  personMetrics: MatchRateMetric | any;
  isLoading: boolean;
};

const MatchRates = ({
  companyMetrics,
  personMetrics,
  isLoading,
}: MatchRatesProps) => {
  const missingDataRate = 1 - personMetrics?.match_rate;
  const badEmailsRate = personMetrics?.missing_with_bad_emails_rate || 0;
  const badEmailsRateQualifier = () => {
    let message = 'some';

    if (badEmailsRate > 0.45 && badEmailsRate < 0.55) {
      message = 'almost half';
    } else if (badEmailsRate > 0.55) {
      message = 'most';
    }

    return message;
  };

  const showBadMatchRate = badEmailsRate > 0;

  return (
    <>
      <HomeMatchRateCard
        data={companyMetrics}
        isLoading={isLoading}
        type="companies"
      >
        <HomeMatchRateCard.Heading>
          Company match rate
        </HomeMatchRateCard.Heading>
      </HomeMatchRateCard>
      <HomeMatchRateCard
        data={personMetrics}
        isLoading={isLoading}
        type="people"
      >
        <HomeMatchRateCard.Heading>
          People match rate{showBadMatchRate ? '*' : ''}
        </HomeMatchRateCard.Heading>
        {showBadMatchRate ? (
          <HomeMatchRateCard.Footer>
            <CKLayoutHorizontal
              alignItems="start"
              gap="0.5rem"
              justifyContent="start"
              wrap="nowrap"
            >
              <span className="basis-auto grow-0 shrink">*</span>
              <p className="shrink">
                For the remaining {Math.round(missingDataRate * 100)}% of email
                addresses, we found that {badEmailsRateQualifier()} (
                {Math.round(badEmailsRate * 100)}%) were not real people (e.g.
                info@ or sales@ email addresses).
              </p>
            </CKLayoutHorizontal>
          </HomeMatchRateCard.Footer>
        ) : null}
      </HomeMatchRateCard>
    </>
  );
};

const CRMAnalysis = () => {
  const { data, loading, refresh } = useMatchRates();
  const [crmAnalysis, setCRMAnalysis] = useState<any>(null);
  const {
    totalRecords,
    setTotalRecords,
    setGeneratedAt,
    setOnRefresh,
  } = useContext(MatchRateContext);

  useEffect(() => {
    if (data) {
      setCRMAnalysis(data);
      setTotalRecords(data.total_records);
      setGeneratedAt(data.generated_at);
      setOnRefresh(() => refresh);
    }
  }, [data]);

  return (
    <MatchRates
      companyMetrics={totalRecords && crmAnalysis?.companies}
      isLoading={loading}
      personMetrics={totalRecords && crmAnalysis?.people}
    />
  );
};

type OnRefreshFunction = () => void;

export const HomeSectionMatchRate = ({
  ...rest
}: HomeSectionMatchRateProps) => {
  //const { hasConnectors } = useOnboardingStatus();
  const [totalRecords, setTotalRecords] = useState<number | undefined>(
    undefined,
  );
  const [generatedAt, setGeneratedAt] = useState<string | undefined>(undefined);
  const [onRefresh, setOnRefresh] = useState<OnRefreshFunction | undefined>(
    undefined,
  );

  return (
    <section {...rest}>
      <MatchRateContext.Provider
        value={{
          totalRecords,
          setTotalRecords,
          generatedAt,
          setGeneratedAt,
          onRefresh,
          setOnRefresh,
        }}
      >
        <HomeBannerMatchRate
          className="mb-10"
          generatedAt={generatedAt}
          onRefresh={onRefresh}
          totalRecords={totalRecords}
        />
        <div className="space-y-8">
          <header>
            <HomeHeading>
              <Target /> Match Rate Analysis
            </HomeHeading>
          </header>
          <CRMAnalysis />
        </div>
      </MatchRateContext.Provider>
    </section>
  );
};
